import {StackScreenProps} from '@react-navigation/stack';
import {
  Box,
  Button,
  Checkbox,
  CheckIcon,
  FormControl,
  VStack,
  WarningOutlineIcon
} from 'native-base';
import React, {useEffect} from 'react';
import {Controller, useForm} from 'react-hook-form';
import {ScrollView} from 'react-native';
import {SafeAreaView} from 'react-native-safe-area-context';
import {t} from 'react-native-tailwindcss';
import {FundsOrigin, UpdateAccountInput, useUpdateAccountMutation} from '../api/generated';
import {Input} from '../components/Input';
import {KeyboardAvoidingView} from '../components/KeyboardAvoidingView';
import {Select, SelectItem} from '../components/Select';
import {useAccountContext} from '../lib/accountContext';
import i18n from '../lib/i18n';
import {Nav, NavParams} from './navigation';

export const EditAccountScreen: React.FC<StackScreenProps<NavParams, Nav.EditAccountScreen>> = ({
  navigation,
  route
}) => {
  const isPayout = route.params?.payout;
  const account = useAccountContext();
  const [updateAccount, {loading}] = useUpdateAccountMutation();
  const defaultValues = {
    notPEP: account?.notPEP || false,
    fundsOrigin: account?.fundsOrigin || undefined,
    occupation: account?.occupation || ''
  };

  const {
    control,
    handleSubmit,
    reset,
    formState: {errors}
  } = useForm({defaultValues});

  useEffect(() => {
    reset(defaultValues);
  }, [account]);

  const onSubmit = async ({occupation, notPEP, fundsOrigin}: UpdateAccountInput) => {
    await updateAccount({
      variables: {
        input: {
          occupation,
          notPEP,
          fundsOrigin
        }
      }
    });
    navigation.navigate(isPayout ? Nav.PayoutScreen : Nav.SettingsScreen);
  };

  return (
    <SafeAreaView style={[t.flex1, t.bgWhite]} edges={['bottom']}>
      <KeyboardAvoidingView>
        <ScrollView style={[t.p6, t.flex1]}>
          <VStack space={3}>
            <FormControl isInvalid={'fundsOrigin' in errors}>
              <FormControl.Label>{i18n.t('fundsOriginDescription')}</FormControl.Label>
              <Controller
                name="fundsOrigin"
                control={control}
                rules={{
                  required: i18n.t('fundsOriginRequired')
                }}
                render={({field: {onChange, onBlur, value}}) => (
                  <Select
                    selectedValue={value}
                    placeholder={i18n.t('fundsOriginPlaceholder')}
                    _selectedItem={{
                      bg: 'gray.50',
                      endIcon: <CheckIcon size="5" />
                    }}
                    onClose={() => onBlur()}
                    onValueChange={(itemValue) => onChange(itemValue)}>
                    <SelectItem label={i18n.t('freelance')} value={FundsOrigin.FREELANCE} />
                    <SelectItem label={i18n.t('salary')} value={FundsOrigin.SALARY} />
                  </Select>
                )}
              />
              <FormControl.ErrorMessage leftIcon={<WarningOutlineIcon size="xs" />}>
                {errors?.fundsOrigin?.message}
              </FormControl.ErrorMessage>
            </FormControl>
            <FormControl isInvalid={'occupation' in errors}>
              <FormControl.Label>{i18n.t('occupationDescription')}</FormControl.Label>
              <Controller
                name="occupation"
                control={control}
                rules={{
                  required: i18n.t('occupationRequired')
                }}
                render={({field: {onChange, onBlur, value}}) => (
                  <Input
                    value={value}
                    onBlur={onBlur}
                    onChangeText={(text) => onChange(text)}
                    returnKeyType="next"
                    onSubmitEditing={handleSubmit(onSubmit)}
                    placeholder={i18n.t('occupationPlaceholder')}
                  />
                )}
              />
              <FormControl.ErrorMessage leftIcon={<WarningOutlineIcon size="xs" />}>
                {errors?.occupation?.message}
              </FormControl.ErrorMessage>
            </FormControl>
            <FormControl isInvalid={'notPEP' in errors}>
              <Controller
                name="notPEP"
                control={control}
                rules={{
                  validate: (value) => value || i18n.t('pepRequired')
                }}
                render={({field: {onChange, value}}) => (
                  <Checkbox value={'yes'} isChecked={value} onChange={onChange}>
                    {i18n.t('pepDescription')}
                  </Checkbox>
                )}
              />
              <FormControl.ErrorMessage leftIcon={<WarningOutlineIcon size="xs" />}>
                {errors?.notPEP?.message}
              </FormControl.ErrorMessage>
            </FormControl>
          </VStack>
        </ScrollView>
        <Box style={t.p6}>
          <Button isLoading={loading} onPress={handleSubmit(onSubmit)}>
            {i18n.t('save')}
          </Button>
        </Box>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
};
